import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Users,
  BarChart3,
  LogOut,
  SlidersHorizontal,
  KeyRound,
  Layers,
  FolderKanban,
  Shield,
  Activity,
  Bell,
  Menu,
  ChevronDown,
  LayoutDashboard,
  Bot,
  Building2,
  FlaskConical,
  Coins,
  MessageSquare,
  Share2,
  Archive,
  Sparkles,
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import Walkthrough from './Walkthrough';
import OrgSwitcher from './OrgSwitcher';
import NotificationsBell from './NotificationsBell';
import { useOrg } from '../contexts/OrgContext';
import { getAppConfig } from '../api';

// Navigation lives in the sidebar, grouped like an AWS-style service list.
// Global / account / utility controls live in the TOP BAR instead.
const NAV_GROUPS = [
  {
    // The org-scoped admin console — visible to an org's owner/admin (and to a
    // platform admin). Each page reads/writes only the ACTIVE org's data
    // (copy-on-write over the built-in defaults).
    label: 'Administration',
    managerOnly: true,
    items: [
      // The full org-scoped admin console — every page reads/writes only the
      // ACTIVE org's data.
      { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', managerOK: true },
      { path: '/users', icon: Users, label: 'Users', managerOK: true },
      { path: '/tiers', icon: Layers, label: 'Tiers', managerOK: true },
      { path: '/projects', icon: FolderKanban, label: 'Teams', managerOK: true },
      { path: '/guardrails', icon: Shield, label: 'Guardrails', managerOK: true },
      { path: '/llm-stats', icon: BarChart3, label: 'Usage Analytics', managerOK: true },
      { path: '/harness-eval', icon: FlaskConical, label: 'Harness Eval', managerOK: true, flag: 'harness_eval' },
      { path: '/settings', icon: SlidersHorizontal, label: 'Settings', managerOK: true },
      { path: '/session-registry', icon: Archive, label: 'Session Registry', managerOK: true },
      { path: '/credit-requests', icon: Coins, label: 'Credit Requests', managerOK: true },
      { path: '/feedback-inbox', icon: MessageSquare, label: 'Feedback', managerOK: true },
    ],
  },
  {
    label: 'Organization',
    adminOnly: false,
    items: [
      { path: '/organizations', icon: Building2, label: 'Organizations' },
      // The roster exposes names and email addresses. Keep it available to
      // organization owners/admins, but never render it for plain members.
      { path: '/members', icon: Users, label: 'Members', managerOnly: true },
    ],
  },
  {
    label: 'My account',
    adminOnly: false,
    items: [
      { path: '/my-projects', icon: FolderKanban, label: 'My Teams' },
      { path: '/my-usage', icon: Activity, label: 'My Usage' },
      { path: '/agent-settings', icon: Bot, label: 'LStack Cloud Agent' },
      { path: '/agents', icon: Sparkles, label: 'Agents' },
      { path: '/shared-sessions', icon: Share2, label: 'Shared Sessions' },
      { path: '/alerts', icon: Bell, label: 'Budget Alerts' },
      { path: '/feedback', icon: MessageSquare, label: 'Send Feedback' },
    ],
  },
];

// path → human label, for the top-bar breadcrumb (longest-prefix match handles
// nested routes like /users/:id/usage).
const ROUTE_LABELS = {
  '/dashboard': 'Overview',
  '/users': 'Users',
  '/tiers': 'Tiers',
  '/projects': 'Teams',
  '/guardrails': 'Guardrails',
  '/llm-stats': 'Usage Analytics',
  '/settings': 'Platform Settings',
  '/session-registry': 'Session Registry',
  '/prompts': 'Prompt Management',
  '/harness-eval': 'Harness Eval',
  '/my-projects': 'My Teams',
  '/my-usage': 'My Usage',
  '/agent-settings': 'LStack Cloud Agent',
  '/agents': 'Agents',
  '/agents/webhooks': 'Agent Webhooks',
  '/agents/schedules': 'Agent Schedules',
  '/agents/repositories': 'Agent Repositories',
  '/organizations': 'Organizations',
  '/members': 'Members',
  '/shared-sessions': 'Shared Sessions',
  '/alerts': 'Budget Alerts',
  '/feedback': 'Send Feedback',
  '/credit-requests': 'Credit Requests',
  '/feedback-inbox': 'Feedback',
  '/account': 'Account',
};

function crumbFor(pathname) {
  const match = Object.keys(ROUTE_LABELS)
    .filter((p) => pathname === p || pathname.startsWith(p + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return match ? ROUTE_LABELS[match] : '';
}

function initials(name, email) {
  const src = (name || email || 'U').trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || 'U') + (parts[1]?.[0] || '')).toUpperCase();
}

function LogoutConfirm({ onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm bg-panel border border-panel-border rounded-2xl p-6 shadow-2xl shadow-black/40"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="logout-title"
      >
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-red-500/10 text-red-400">
            <LogOut className="w-5 h-5" />
          </span>
          <h2 id="logout-title" className="font-display text-lg font-bold text-fg">Log out?</h2>
        </div>
        <p className="text-sm text-fg-muted mb-6">
          You'll need to sign in again to access your dashboard. Your API key keeps working.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-lg border border-panel-border text-fg hover:border-fg-muted text-sm font-medium transition-colors">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors">Log out</button>
        </div>
      </div>
    </div>
  );
}

// Top-bar account dropdown (name/email + admin badge + account settings + logout).
function AccountMenu({ name, email, isAdmin, onLogout }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-fg/5 transition-colors"
      >
        <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-orange/15 text-orange text-xs font-bold">
          {initials(name, email)}
        </span>
        <span className="hidden sm:block text-left leading-tight max-w-[140px]">
          <span className="block text-sm text-fg truncate">{name || 'Account'}</span>
          <span className="block text-[11px] text-fg-muted truncate">{email}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-fg-muted hidden sm:block" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-64 z-50 rounded-xl border border-panel-border bg-panel shadow-2xl shadow-black/40 overflow-hidden">
            <div className="px-4 py-3 border-b border-panel-border">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-fg truncate">{name || 'Account'}</span>
                {isAdmin && <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange/15 text-orange">Admin</span>}
              </div>
              <div className="text-xs text-fg-muted truncate mt-0.5">{email}</div>
            </div>
            <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-fg hover:bg-fg/5 transition-colors">
              <KeyRound className="w-4 h-4 text-fg-muted" /> Account settings
            </Link>
            <Link to="/alerts" onClick={() => setOpen(false)} className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-fg hover:bg-fg/5 transition-colors">
              <Bell className="w-4 h-4 text-fg-muted" /> Budget alerts
            </Link>
            <button onClick={() => { setOpen(false); onLogout(); }} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors border-t border-panel-border">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('lstack_sidebar_collapsed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const handler = () => setShowWalkthrough(true);
    window.addEventListener('lstack:open-tour', handler);
    return () => window.removeEventListener('lstack:open-tour', handler);
  }, []);

  const adminEmail = localStorage.getItem('admin_email');
  const adminName = localStorage.getItem('admin_name');
  const isAdmin = localStorage.getItem('admin_is_admin') === 'true';
  const { active: activeOrg, resolved: orgResolved, isManager: orgManager } = useOrg();

  const toggleSidebar = () => setCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem('lstack_sidebar_collapsed', nv ? '1' : '0'); } catch { /* ignore */ }
    return nv;
  });

  const doLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('admin_is_admin');
    localStorage.removeItem('active_org_id');
    localStorage.removeItem('active_org_name');
    navigate('/login');
  };

  // Server-side feature flags. A tool the deployment has disabled should not
  // show a nav entry that leads to a page whose routes are not mounted.
  const [flags, setFlags] = useState({});
  useEffect(() => {
    let alive = true;
    getAppConfig().then((r) => alive && setFlags(r.data || {})).catch(() => {});
    return () => { alive = false; };
  }, []);
  const showGroup = (g) => {
    if (g.adminOnly) return isAdmin;   // platform admin only
    // Org console: the caller's role in the ACTIVE org decides, and only that.
    // Platform admins are not exempt — being a plain member of an org means
    // seeing what a member sees, here and in the API alike.
    if (g.managerOnly) return orgManager;
    return true;                       // everyone signed in
  };
  const groups = NAV_GROUPS
    .filter(showGroup)
    .map((g) => {
      let items = g.items;
      // A managerOnly group is already gated on the org role, and every page
      // in it is org-scoped — `managerOK` marks the ones that have been
      // confirmed as such, so anything unmarked stays out.
      if (g.managerOnly) items = items.filter((it) => it.managerOK);
      items = items.filter((it) => !it.managerOnly || orgManager);
      items = items.filter((it) => !it.flag || flags[it.flag]);
      return { ...g, items };
    })
    .filter((g) => g.items.length > 0);

  const crumb = crumbFor(location.pathname);

  return (
    <div className="flex flex-col h-screen bg-surface text-fg">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={toggleSidebar} title="Toggle sidebar" className="p-2 rounded-lg text-fg-muted hover:text-fg hover:bg-fg/5 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <Logo size={24} />
          <div className="w-px h-6 bg-panel-border mx-1 hidden sm:block" />
          <OrgSwitcher />
          {crumb && (
            <span className="hidden md:flex items-center gap-2 text-sm text-fg-muted min-w-0">
              <span className="text-panel-border">/</span>
              <span className="text-fg truncate">{crumb}</span>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <NotificationsBell />
          <ThemeToggle />
          <div className="w-px h-6 bg-panel-border mx-1 hidden sm:block" />
          <AccountMenu name={adminName} email={adminEmail} isAdmin={isAdmin} onLogout={() => setConfirmLogout(true)} />
        </div>
      </header>

      {/* ── Body: sidebar + content ─────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">
        <aside className={`${collapsed ? 'w-16' : 'w-60'} shrink-0 bg-panel border-r border-panel-border flex flex-col transition-[width] duration-200`}>
          <nav className="flex-1 py-4 overflow-y-auto">
            {groups.map((group) => (
              <div key={group.label} className="mb-4">
                {!collapsed && (
                  <div className="px-5 mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-fg-muted/70">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) =>
                      `flex items-center ${collapsed ? 'justify-center px-0' : 'px-5'} py-2.5 text-sm transition-colors ${
                        isActive
                          ? 'text-orange bg-orange/10 border-r-2 border-orange'
                          : 'text-fg-muted hover:text-fg hover:bg-fg/5'
                      }`
                    }
                  >
                    <item.icon className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
                    {!collapsed && item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <main className="flex-1 overflow-auto">
          <div className="p-8">
            {orgResolved ? <Outlet key={activeOrg?.org_id || 'no-org'} /> : null}
          </div>
        </main>
      </div>

      {confirmLogout && (
        <LogoutConfirm onCancel={() => setConfirmLogout(false)} onConfirm={doLogout} />
      )}
      <Walkthrough forceOpen={showWalkthrough} onClose={() => setShowWalkthrough(false)} />
    </div>
  );
}

export default Layout;
